/**
 * One client store, kept across tabs and across launches (X1).
 *
 * Every screen used to own its fetch. Five tabs meant five copies of the feed
 * in memory and a full re-pull every time one of them was reached, and a write
 * on any of them re-pulled the lot through `useAction`'s refetch. This is the
 * one cache they all read, persisted to IndexedDB so a cold start paints from
 * what the last one already knew.
 *
 * ## The retry is load-bearing, and it is not about the network
 *
 * `useQuery.ts` — which this package deletes — retried a fixed set of errors
 * twice with a linear backoff, and WP 1.1 depends on it: `App.tsx` now paints
 * the shell *before* `getSession()` resolves, which is only safe because a
 * screen whose first query loses the race with the session retries instead of
 * rendering "Could not read". `TRANSIENT` below is that regex, moved rather
 * than rewritten.
 *
 * `networkMode: 'offlineFirst'` does not cover it. A JWT rejection is a
 * successful HTTP response carrying an error body, not a network failure, so
 * nothing about the connection state tells the client to ask again.
 */
import { createElement, useEffect, useState, type ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  persistQueryClient,
  type PersistedClient,
  type Persister,
} from '@tanstack/query-persist-client-core'
import { del, get, set } from 'idb-keyval'
import {
  LEGACY_CACHE_KEY, cacheKeyFor, clearUserStorage, readStoredUserId,
} from './userStorage'

/**
 * Errors that mean "ask again in a moment", not "this failed".
 *
 * Verbatim from `useQuery.ts:19`, along with its reasoning, because the
 * reasoning is the only thing that explains why `JWT` is in a list beside
 * `NetworkError`:
 *
 * > A token refreshed a second ago can be rejected as `issued at future`: the
 * > `iat` claim is stamped by the auth server and checked by PostgREST, and a
 * > fraction of a second of clock skew between them is enough. It showed up
 * > only on Home, and only on a cold load, because Home is the landing tab and
 * > fires seven queries the instant it mounts — every other screen is reached
 * > later, by which time the token has aged into validity.
 * >
 * > Surfacing that as "Could not read" is wrong twice over: it is not a
 * > failure, and the suggested cause — unapplied migrations — sent me looking
 * > in the wrong place.
 */
const TRANSIENT = /issued at future|JWT|JWS|token is expired|fetch failed|NetworkError|Failed to fetch/i

/** Two retries, and a backoff that is linear rather than exponential: the
 *  window this closes is under a second, and a slow retry would be as visible
 *  as the error was. Same two numbers `useQuery.ts` used. */
const RETRIES = 2
const BACKOFF_MS = 400

const message = (e: unknown): string =>
  e instanceof Error ? e.message : typeof e === 'string' ? e : ''

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
      gcTime: 7 * 24 * 60 * 60_000,
      refetchOnWindowFocus: true,
      networkMode: 'offlineFirst',
      retry: (failureCount, error) => failureCount < RETRIES && TRANSIENT.test(message(error)),
      retryDelay: attempt => BACKOFF_MS * (attempt + 1),
    },
    mutations: {
      networkMode: 'offlineFirst',
      // A write is not retried. An upsert would survive it, but `addCommitment`
      // and `addItem` are inserts and a retried insert is a duplicate row.
      retry: 0,
    },
  },
})

/**
 * Bumped when a query's shape changes, so a persisted cache written by an
 * older bundle is dropped rather than hydrated into code that no longer
 * understands it. WP 1.4b swaps `['feed']`'s cached value from a plain
 * `FeedRow[]` to `{ as_of, rows }` (`feed_since`'s own cursor alongside the
 * rows) — a v1 cache hydrated under the new code would have `deriveFeed`
 * mapping over `cache.rows`, i.e. `undefined`, on every screen at once.
 *
 * v3, nightlife N7: `feed_page` sends `night` and `series` from
 * 20260930170000. A warm cache only ever receives rows whose
 * `entries.updated_at` moved after its `as_of`, and `entry_series` edges
 * written by the backfill (`/backfill-people`) — or by a harvest between its
 * entry upsert and its edge write — do not move `entries.updated_at`, so a
 * warm cache would never receive `series` otherwise: every club night in it
 * would stay partyless until something else about the row changed. The
 * bump drops every persisted cache once, and 20260930170100's trigger moves
 * `updated_at` on every edge written after it. CLAUDE.md's E1/E5 trap; the
 * reads are `?? null` / `?? []` all the same, for the minutes between a
 * deploy and a reload.
 *
 * v4, cities plan C2.2: the feed is keyed `['feed', regionId]`, one cache and
 * one `as_of` per region. A v3 cache holds the old `['feed']` — about 6 MB
 * that no reader would ever ask for again, orphaned until `gcTime` — and a
 * `['news']` / `['weather']` that are Berlin's under keys that now mean
 * something else. The bump drops them once. It is the only bump in the cities
 * plan: C2.1 and C2.3 do not take one.
 */
export const CACHE_BUSTER = 'v4'

/** How long a persisted cache is worth hydrating. Matched to `gcTime` rather
 *  than left at the library's 24 hours, which would have thrown away a
 *  week-old cache the client itself still considered live. */
const MAX_AGE_MS = 7 * 24 * 60 * 60_000

/** The feed alone is about 6 MB of JSON, and `persistQueryClientSubscribe`
 *  offers to write the whole cache on every cache event — every optimistic
 *  patch included. Structured-cloning that into IndexedDB on each keystroke
 *  of a filter is not worth the durability, so writes trail by this much and
 *  the last state wins. */
const WRITE_THROTTLE_MS = 2_000

/**
 * Strips `context` off every dehydrated mutation before it is handed to
 * IndexedDB.
 *
 * IndexedDB clones with the structured-clone algorithm, which has no way to
 * clone a function — and every `onMutate` in `mutations.ts` returns one
 * (`{ undo: () => void }`), stored as the mutation's own `context`.
 * `dehydrateMutation` (query-core) copies `mutation.state` whole, so a paused
 * write's context rides along into the same snapshot as the query cache, and
 * `idb-keyval`'s `set` — a single `IDBObjectStore.put` for the *whole*
 * `PersistedClient` — throws `DataCloneError` the instant one is in there.
 * Unhandled, that would fail every write to IndexedDB for as long as any
 * mutation stays paused, queries included, and `flush`'s own `.catch(() => {})`
 * would swallow it: the persisted cache would quietly stop updating for the
 * entire outage rather than visibly breaking on the one write that has the
 * problem.
 *
 * Losing `context` on a resumed mutation costs the rollback only — the
 * optimistic patch itself already lives in the query cache (`patch()` in
 * `mutations.ts` writes it straight into `entry_state` and friends), so a
 * write that replays and fails after a reload can't undo its own optimistic
 * patch, but everything about the write surviving the reload at all does not
 * depend on it.
 */
function sanitize(client: PersistedClient): PersistedClient {
  return {
    ...client,
    clientState: {
      ...client.clientState,
      mutations: client.clientState.mutations.map(m => ({ ...m, state: { ...m.state, context: undefined } })),
    },
  }
}

/** IndexedDB rather than `localStorage`: the cache is megabytes, and rule 7
 *  in CLAUDE.md is that `localStorage` holds the signed-in session and is
 *  never cleared. Nothing here touches it.
 *
 *  One persister per user (U0.4): `key` is `cacheKeyFor(uid)`. `dispose()`
 *  retires it when the user changes, and a retired persister neither writes
 *  nor restores — a throttled write that fires after the switch, or a restore
 *  whose read was already in flight, would otherwise put the last user's
 *  cache back into the next user's client. */
function idbPersister(key: string) {
  let timer: ReturnType<typeof setTimeout> | null = null
  let pending: PersistedClient | null = null
  let disposed = false

  const flush = () => {
    timer = null
    const client = pending
    pending = null
    if (client && !disposed) void set(key, sanitize(client)).catch(() => {})
  }

  const persister: Persister & { dispose: () => void } = {
    persistClient: client => {
      if (disposed) return
      pending = client
      if (timer) return
      timer = setTimeout(flush, WRITE_THROTTLE_MS)
    },
    restoreClient: async () => {
      const v = await get<PersistedClient>(key)
      return disposed ? undefined : v
    },
    removeClient: async () => {
      if (timer) { clearTimeout(timer); timer = null }
      pending = null
      await del(key)
    },
    dispose: () => {
      disposed = true
      if (timer) { clearTimeout(timer); timer = null }
      pending = null
    },
  }
  return persister
}

/** A private window, a browser with site data blocked, or a first-ever load
 *  all make the read either fail or hang. Neither may hold the app back, so
 *  the restore is raced against a ceiling and the app starts cold if it
 *  loses. A late restore cannot clobber anything: `hydrate` only writes a
 *  query whose persisted `dataUpdatedAt` is newer than what the cache holds,
 *  and a persisted row is by definition older than one just fetched. */
const RESTORE_CEILING_MS = 2_000

let restored = typeof indexedDB === 'undefined'

/**
 * Whose cache this is (U0.4). `activeUid` is the user the running persister
 * was created for; `onAuthUser` below moves it when the session says someone
 * else is here.
 *
 * Read synchronously from the Supabase token in localStorage rather than
 * after `getSession()` resolves. WP 1.1 paints the shell before the session
 * round trip precisely so a cold load does not wait on it, and a persister
 * created after it would put that wait back in front of the one IndexedDB read
 * the app does hold children for. The stored id is a guess the session
 * confirms (usually the same answer); if it is wrong, the store re-keys and
 * the cost is a cold cache, never a wrong user's lists, because the key and
 * the session disagree only when something else changed.
 */
let activeUid: string | null = readStoredUserId(import.meta.env.VITE_SUPABASE_URL as string | undefined)
let generation = 0
let retire: (() => void) | null = null

/** Creates a persister under `uid`'s key and starts restoring it. Resolves
 *  when the restore has landed, or the ceiling passes. */
function startPersistence(uid: string | null): Promise<void> {
  const persister = idbPersister(cacheKeyFor(uid))
  const [unsubscribe, done] = persistQueryClient({
    queryClient,
    persister,
    buster: CACHE_BUSTER,
    maxAge: MAX_AGE_MS,
  })
  retire = () => { unsubscribe(); persister.dispose() }
  const ceiling = new Promise<void>(r => setTimeout(r, RESTORE_CEILING_MS))
  return Promise.race([done.catch(() => {}), ceiling])
}

/**
 * `restoring`'s own hydrate can rebuild a mutation that was already paused —
 * offline, mid-write — when the tab closed. That mutation is not paused *now*
 * in any sense `mount()` below would ever see: there is no `online` or
 * `focus` edge left to fire, since nothing changed after this load started.
 * X11 resumes it explicitly instead, once, right here — the one transition
 * `mount()`'s own subscriptions cannot cover.
 *
 * A later, genuine reconnect needs no equivalent call: `QueryClientProvider`
 * calls `queryClient.mount()` on mount, which subscribes to `online` and
 * `focus` and already calls `resumePausedMutations()` on both — see
 * `queryClient.ts`'s own `mount()` in `@tanstack/query-core`. Duplicating
 * that subscription here would only call the same method twice.
 */
const restoring: Promise<void> = (() => {
  if (restored) return Promise.resolve()
  // The pre-U0.4 key is unread from here on; drop it once so a phone does not
  // hold two seven-day caches.
  void del(LEGACY_CACHE_KEY).catch(() => {})
  return startPersistence(activeUid).then(() => {
    restored = true
    void queryClient.resumePausedMutations()
  })
})()

/**
 * Tells the store who is signed in. `auth.tsx` calls this from
 * `onAuthStateChange`, so there is one place a user change is handled.
 *
 * A change of user — sign-out, or a session for someone other than the user
 * the persister was created for — drops everything the last user left in this
 * client: the in-memory queries, the mutation cache (and with it every paused
 * write, which would otherwise replay under the new user's JWT), and the last
 * user's IndexedDB and storage keys. Then a persister is created under the
 * new user's key, which hydrates *their* earlier cache if this device has one.
 *
 * Order matters. The old persister is retired *before* the clear: clearing
 * the query cache emits cache events, and a live subscriber would write the
 * now-empty client over the key we are about to delete (or, worse, over the
 * wrong one).
 *
 * `uid === null` only counts as a sign-out for `SIGNED_OUT` and an
 * `INITIAL_SESSION` with no session; a `TOKEN_REFRESHED` or the like never
 * carries less than it had.
 */
export function onAuthUser(event: string, uid: string | null): void {
  if (uid === null && event !== 'SIGNED_OUT' && event !== 'INITIAL_SESSION') return
  if (uid === activeUid) return
  const prev = activeUid
  activeUid = uid
  const mine = ++generation
  retire?.()
  retire = null
  queryClient.clear()
  queryClient.getMutationCache().clear()
  const cleared = clearUserStorage(prev)
  if (typeof indexedDB === 'undefined') return
  void cleared.then(() => {
    if (mine !== generation) return
    return startPersistence(uid).then(() => {
      if (mine === generation) void queryClient.resumePausedMutations()
    })
  })
}

/** Writes that are queued, not sent: what a sign-out would throw away. */
export function unsentChangeCount(): number {
  return queryClient.getMutationCache().getAll().filter(m => m.state.isPaused).length
}

/**
 * The provider, and the one beat the app waits on.
 *
 * Children are held back until the persisted cache has been read, because a
 * screen that mounts first and fetches second is exactly the request this
 * package exists to remove — the restore would land after it and the network
 * round trip would have been paid for nothing. The wait is one IndexedDB read
 * and is bounded by `RESTORE_CEILING_MS`; it is not the session round trip WP
 * 1.1 moved the shell in front of.
 */
export function StoreProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(restored)

  useEffect(() => {
    if (ready) return
    let live = true
    void restoring.then(() => { if (live) setReady(true) })
    return () => { live = false }
  }, [ready])

  return createElement(QueryClientProvider, { client: queryClient }, ready ? children : null)
}
